import { gridProductKey } from "../keys";
import { hiddenModeRefusal, isEmptyString } from "../validations";
import { readHidden, writeHidden } from "../visibility";
import type { CustomizationHandler } from "./types";

/**
 * The compare-at price on every product-grid card: category, search and home
 * listings, and the product page's related products. The counterpart of
 * `product-detail-compare-at-price`, and independent of it.
 *
 * Same scope as `product-grid-item-price`: Core themes mark each card with
 * `data-product-type="list"`, in all 24 with NubeSDK slots, whether the card
 * is the theme's own template or Core's `product-item` component (which gets
 * the `js-compare-price-display` class passed in by bahia, cali, cubo, flex,
 * new_linkedman and style). The product page and the quick shop never carry
 * it, which keeps this apart from `product-detail-compare-at-price`.
 *
 * Every card shows a different compare-at price, so apps usually pass a
 * resolver for `text`, which `identify` maps back to each card through its
 * product id. `hidden` is usually the same for every card, as plain options.
 *
 * The themes' quick-shop JS calls `.show()` / `.hide()` on this element on
 * variant selection, an inline `display` that the `hidden` class outranks
 * with `!important`. See ../visibility.ts.
 *
 * @see ./README.md
 */
export const productGridItemCompareAtPrice: CustomizationHandler<"product-grid-item-compare-at-price"> =
	{
		host: "storefront",
		selector: '[data-product-type="list"] .js-compare-price-display',

		read(element) {
			// As with `product-detail-compare-at-price`: the theme's inline
			// `display` is not part of the snapshot. See ../visibility.ts.
			return {
				text: element.textContent ?? "",
				hidden: readHidden(element),
			};
		},

		operation(element, options) {
			// `textContent` only: the numeric prices the theme computes from
			// live in its variant data, which this leaves untouched.
			if (options.text !== undefined) element.textContent = options.text;
			if (options.hidden !== undefined) writeHidden(element, options.hidden);
		},

		identify: gridProductKey,

		validate(_element, options) {
			if (options.text !== undefined && isEmptyString(options.text)) {
				return 'refused "text": a price cannot be blank';
			}
			if (options.hidden !== undefined) {
				const refusal = hiddenModeRefusal(options.hidden);
				if (refusal) return `refused "hidden": ${refusal}`;
			}

			return null;
		},
	};
